import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  AlertTriangle, CheckCircle2, XCircle, Printer, ArrowLeft, Plus, FileText, Pencil, FilePlus2,
} from "lucide-react";
import { formatDate } from "../lib/utils";
import AssessmentHistory from "../components/AssessmentHistory";
import GenerateSuiteDialog from "../components/documents/GenerateSuiteDialog";

function AuditBadge({ type }) {
  const cert = type === "CERTIFICATION";
  return (
    <span
      className={`inline-flex items-center font-semibold text-xs px-3 py-1 rounded ${
        cert ? "bg-[#0D1B2A] text-white" : "bg-[#1A7F6E] text-white"
      }`}
    >
      {cert ? "CERTIFICATION" : "VERIFICATION"}
    </span>
  );
}

export default function ReportPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openGenerate, setOpenGenerate] = useState(false);

  useEffect(() => {
    api.get(`/assessments/${id}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="text-sm text-slate-500">Loading report…</div>;
  if (!data)
    return (
      <div className="text-center py-16">
        <p className="text-sm text-slate-500">Assessment not found.</p>
        <Link to="/" className="text-[#1A7F6E] hover:underline text-sm">← Back to dashboard</Link>
      </div>
    );

  const { input, result, created_at } = data;

  return (
    <div className="space-y-6" data-testid="report-page">
      {/* toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 no-print">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => nav("/")} data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" />Dashboard
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/assessment/${id}/edit`}>
            <Button variant="outline" data-testid="edit-assessment-button">
              <Pencil className="w-4 h-4 mr-2" />Edit
            </Button>
          </Link>
          {data.lead_id && (
            <Button
              variant="outline"
              onClick={() => setOpenGenerate(true)}
              data-testid="generate-suite-btn-report"
            >
              <FilePlus2 className="w-4 h-4 mr-2" />Generate document suite
            </Button>
          )}
          <Link to="/new">
            <Button variant="outline" data-testid="new-assessment-button-report">
              <Plus className="w-4 h-4 mr-2" />New assessment
            </Button>
          </Link>
          <Button
            onClick={() => window.print()}
            className="bg-[#0D1B2A] hover:bg-[#070e17] text-white"
            data-testid="print-pdf-button"
          >
            <Printer className="w-4 h-4 mr-2" />Print / Save as PDF
          </Button>
        </div>
      </div>

      {data.lead_id && (
        <GenerateSuiteDialog
          open={openGenerate}
          onOpenChange={setOpenGenerate}
          leadId={data.lead_id}
        />
      )}

      {/* header */}
      <Card className="border-slate-200 shadow-sm avoid-break">
        <CardContent className="p-8">
          <div className="flex flex-wrap justify-between gap-6">
            <div>
              <div className="wordmark text-lg text-[#0D1B2A] flex items-center mb-4">
                <span className="np-mark">NP</span>Next Provider
              </div>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Discovery report</p>
              <h1 className="text-3xl font-semibold tracking-tight text-[#0D1B2A]" data-testid="report-org-name">
                {input.org_name}
              </h1>
              <div className="mt-2 text-sm text-slate-500 space-x-4">
                {input.abn && <span>ABN {input.abn}</span>}
                <span>Discovery {input.discovery_date || formatDate(created_at)}</span>
                {input.target_registration_date && <span>Target rego {input.target_registration_date}</span>}
              </div>
            </div>
            <div className="text-right space-y-2">
              <AuditBadge type={result.audit_type} />
              <p className="text-sm text-slate-500">Estimated audit cost</p>
              <p className="text-lg font-semibold text-[#0D1B2A]" data-testid="report-cost">{result.cost_range}</p>
              <p className="text-xs text-slate-500">Timeline: {result.timeline}</p>
            </div>
          </div>
          <div className="mt-6 p-4 bg-[#E6F4F1] rounded-md">
            <p className="text-xs uppercase tracking-[0.2em] text-[#1A7F6E] mb-1">Recommended engagement</p>
            <p className="text-base font-semibold text-[#0D1B2A]" data-testid="report-engagement">{result.engagement_type}</p>
          </div>
        </CardContent>
      </Card>

      {/* modules */}
      <SectionCard title="Required modules" step="01" testid="section-modules">
        <div className="grid sm:grid-cols-2 gap-4">
          {result.modules.map((m) => (
            <div key={m.code} className="border border-slate-200 rounded-md p-4 avoid-break">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold bg-[#0D1B2A] text-white px-2 py-0.5 rounded">{m.code}</span>
                <span className="text-sm font-semibold text-[#0D1B2A]">{m.name}</span>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{m.description}</p>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* red flags */}
      {result.red_flags.length > 0 && (
        <SectionCard title="Red flags" step="02" testid="section-red-flags">
          <div className="space-y-3">
            {result.red_flags.map((f) => (
              <div
                key={f}
                className="bg-red-50 border border-red-200 border-l-4 border-l-[#DC2626] p-4 rounded flex items-start gap-3 avoid-break"
              >
                <AlertTriangle className="w-5 h-5 text-[#DC2626] shrink-0 mt-0.5" />
                <p className="text-sm text-[#7F1D1D] leading-relaxed">{f}</p>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* missing docs */}
      <SectionCard title="Missing documents" step="03" testid="section-missing-docs">
        {result.missing_documents.length === 0 ? (
          <p className="text-sm text-slate-500">All critical documents held. Verify currency.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-2">
            {result.missing_documents.map((d) => (
              <div key={d.key} className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded avoid-break">
                <XCircle className="w-4 h-4 text-[#DC2626] shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-[#7F1D1D]">{d.name}</p>
                  <p className="text-xs text-red-700/70">{d.group}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* held docs */}
      <SectionCard title="Documents held — verify currency" step="04" testid="section-held-docs">
        {result.held_documents.length === 0 ? (
          <p className="text-sm text-slate-500">No documents ticked as held.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-2">
            {result.held_documents.map((d) => (
              <div key={d.key} className="flex items-start gap-2 p-3 bg-[#E6F4F1] border border-[#1A7F6E]/30 rounded avoid-break">
                <CheckCircle2 className="w-4 h-4 text-[#1A7F6E] shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-[#0D1B2A]">{d.name}</p>
                  <p className="text-xs text-slate-500">{d.group}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* selected groups */}
      {result.selected_groups?.length > 0 && (
        <SectionCard title="Selected registration groups" step="05" testid="section-groups">
          <div className="grid sm:grid-cols-2 gap-2">
            {result.selected_groups.map((g) => (
              <div key={g.key} className="p-3 border border-slate-200 rounded flex items-start gap-2 avoid-break">
                <FileText className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-[#0D1B2A]">{g.code}</span>
                    <span className="text-sm text-slate-700">{g.name}</span>
                    <span
                      className={`text-[10px] font-semibold px-2 py-0.5 rounded ${
                        g.cert ? "bg-[#0D1B2A] text-white" : "bg-[#1A7F6E] text-white"
                      }`}
                    >
                      {g.cert ? "CERT" : "VERIF"}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{g.category}</p>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* templates */}
      <SectionCard title="Operational templates to build / populate" step="06" testid="section-templates">
        <ul className="grid sm:grid-cols-2 gap-y-1.5 gap-x-4 list-disc pl-5 text-sm text-slate-700">
          {result.templates.map((t) => (<li key={t}>{t}</li>))}
        </ul>
      </SectionCard>

      {/* training */}
      <SectionCard title="Mandatory training requirements" step="07" testid="section-training">
        <ul className="grid sm:grid-cols-2 gap-y-1.5 gap-x-4 list-disc pl-5 text-sm text-slate-700">
          {result.training.map((t) => (<li key={t}>{t}</li>))}
        </ul>
      </SectionCard>

      <AssessmentHistory assessmentId={id} />

      <div className="text-xs text-slate-400 text-center pt-4">
        Prepared by Next Provider Australia — nextprovider.com.au · {formatDate(created_at)}
      </div>
    </div>
  );
}

function SectionCard({ step, title, testid, children }) {
  return (
    <Card className="border-slate-200 shadow-sm avoid-break" data-testid={testid}>
      <CardContent className="p-6">
        <div className="mb-4 pb-4 border-b border-slate-100">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Section {step}</p>
          <h2 className="text-xl font-semibold tracking-tight text-[#0D1B2A]">{title}</h2>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}
